/**
 * In-memory mock of the kiosk PIN commands, mirroring the ACC contract so the
 * kiosk page and the manager panel run offline.
 *
 * Failures resolve to `{success:false, error, errorCode, details}` (the Lambda's
 * envelope) because the pages branch on `errorCode`; a thrown Error would lose it.
 * Seed PINs are throwaway `0000`-style values and never leave this file.
 */

import { getUserName } from '../Utils/GeneralUtils';
import { isKioskSession } from '../kioskFlag';

const NETWORK_DELAY_MS = 120;
const KIOSK_SESSION_MS = 12 * 60 * 60 * 1000;
const LOCK_AFTER_WRONG = 5;
const LOCK_MS = 15 * 60 * 1000;
const PIN_FORMAT = /^\d{4,6}$/;
const KIOSK_ROLE = 'HeroTrainer';
const NOT_ON_KIOSK = 'Not available on the kiosk.';

const nowMs = () => Date.now();
const iso = (ms) => new Date(ms).toISOString();

const SEED_ROSTER = ['Alex Rivera', 'Priya Patel', 'Sam Okafor', 'Maria Gomez', 'Devin Brooks', 'Jordan Lee'];

const seed = () => {
    const t = nowMs();
    return {
        enabled: true,
        sessionSeq: 0,
        roster: [...SEED_ROSTER],
        pins: {
            'Alex Rivera': { pin: '0000', updatedAt: iso(t - 6 * 86400000), updatedBy: 'Jordan Lee', failedCount: 0, lockedUntil: '' },
            'Priya Patel': { pin: '1111', updatedAt: iso(t - 3 * 86400000), updatedBy: 'Jordan Lee', failedCount: 0, lockedUntil: '' },
            'Sam Okafor': { pin: '2222', updatedAt: iso(t - 9 * 86400000), updatedBy: 'Jordan Lee', failedCount: 0, lockedUntil: iso(t + LOCK_MS) },
        },
        signins: [
            { trainer: 'Alex Rivera', signedInAt: iso(t - 26 * 3600000), gymId: 'Arena One', sourceIp: '10.0.0.5' },
            { trainer: 'Priya Patel', signedInAt: iso(t - 5 * 3600000), gymId: 'Arena One', sourceIp: '10.0.0.5' },
            { trainer: 'Alex Rivera', signedInAt: iso(t - 2 * 3600000), gymId: 'Arena One', sourceIp: '10.0.0.5' },
        ],
    };
};

let store = seed();

export const resetMockKiosk = () => {
    store = seed();
};

export const setMockKioskEnabled = (value) => {
    store.enabled = Boolean(value);
};

/** Fixture for tests that need a trainer to be locked, or not. */
export const setMockTrainerLock = (trainer, lockedUntilMs) => {
    const row = store.pins[trainer];
    if (!row) return;
    row.lockedUntil = lockedUntilMs ? iso(lockedUntilMs) : '';
    row.failedCount = 0;
};

const fail = (message, code = null, details = null) => {
    const error = new Error(message);
    error.errorCode = code;
    error.details = details;
    return error;
};

/** The server forces the HeroTrainer role on a kiosk session and 403s the PIN admin commands. */
const refuseOnKiosk = () => {
    if (isKioskSession()) throw fail(NOT_ON_KIOSK, 'E_SESSION_REQUIRED');
};

const text = (value) => String(value ?? '').trim();

const requirePin = (pin) => {
    if (!PIN_FORMAT.test(String(pin ?? ''))) throw fail('PIN must be 4 to 6 digits.', 'E_PIN_FORMAT');
};

const lockedUntilMs = (row) => {
    const ms = row?.lockedUntil ? Date.parse(row.lockedUntil) : NaN;
    return Number.isFinite(ms) && ms > nowMs() ? ms : null;
};

const pinView = (trainer) => {
    const row = store.pins[trainer];
    return {
        trainer,
        hasPin: Boolean(row),
        updatedAt: row?.updatedAt || null,
        updatedBy: row?.updatedBy || null,
        lockedUntil: lockedUntilMs(row) ? row.lockedUntil : '',
        failedCount: row?.failedCount || 0,
    };
};

const handlers = {
    list_kiosk_trainers: () => {
        if (!store.enabled) return { enabled: false, trainers: [] };
        const trainers = Object.keys(store.pins)
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({ name, hasPin: true }));
        return { enabled: true, trainers };
    },

    start_kiosk_session: ([name, pin] = []) => {
        if (!store.enabled) throw fail('Kiosk is off.', 'E_KIOSK_DISABLED');
        // The server does not tell a malformed PIN from a wrong one: the answer must not say which.
        if (!PIN_FORMAT.test(String(pin ?? ''))) throw fail('Name or PIN did not match.', 'E_PIN_WRONG');
        const row = store.pins[text(name)];
        if (!row) throw fail('Name or PIN did not match.', 'E_PIN_WRONG');
        const lockedMs = lockedUntilMs(row);
        if (lockedMs) throw fail('Too many tries.', 'E_PIN_LOCKED', { until: iso(lockedMs) });
        if (row.pin !== String(pin)) {
            row.failedCount += 1;
            // The lock restarts the count, so a locked trainer reports no tries.
            if (row.failedCount >= LOCK_AFTER_WRONG) {
                row.lockedUntil = iso(nowMs() + LOCK_MS);
                row.failedCount = 0;
            }
            throw fail('Name or PIN did not match.', 'E_PIN_WRONG');
        }
        row.failedCount = 0;
        row.lockedUntil = '';
        store.sessionSeq += 1;
        store.signins.push({ trainer: text(name), signedInAt: iso(nowMs()), gymId: 'Arena One', sourceIp: '10.0.0.5' });
        return {
            session: `mock-kiosk-session-${store.sessionSeq}`,
            trainer: text(name),
            expiresAt: iso(nowMs() + KIOSK_SESSION_MS),
            role: KIOSK_ROLE,
        };
    },

    set_trainer_pin: ([trainer, pin] = []) => {
        refuseOnKiosk();
        const name = text(trainer);
        if (!name) throw fail('Trainer is required.');
        requirePin(pin);
        if (!store.roster.includes(name)) throw fail('Unknown trainer.', 'E_TRAINER_UNKNOWN');
        store.pins[name] = {
            pin: String(pin),
            updatedAt: iso(nowMs()),
            updatedBy: getUserName() || 'Mock Admin',
            failedCount: 0,
            lockedUntil: '',
        };
        return pinView(name);
    },

    clear_trainer_pin: ([trainer] = []) => {
        refuseOnKiosk();
        const name = text(trainer);
        delete store.pins[name];
        return pinView(name);
    },

    list_trainer_pins: () => {
        refuseOnKiosk();
        const names = [...new Set([...store.roster, ...Object.keys(store.pins)])];
        return { pins: names.sort((a, b) => a.localeCompare(b)).map(pinView) };
    },

    list_kiosk_signins: ([trainer, days] = []) => {
        refuseOnKiosk();
        const since = days ? nowMs() - Number(days) * 86400000 : 0;
        const signins = store.signins
            .filter((row) => (!trainer || row.trainer === trainer) && Date.parse(row.signedInAt) >= since)
            .sort((a, b) => Date.parse(b.signedInAt) - Date.parse(a.signedInAt));
        return { signins };
    },
};

/** Drop-in for `executeCommand`. */
export const mockKioskExecuteCommand = (command, args = []) =>
    new Promise((resolve) => {
        setTimeout(() => {
            const handler = handlers[command];
            if (!handler) {
                resolve({ success: false, error: `Mock: unknown command "${command}".`, errorCode: null, details: null });
                return;
            }
            try {
                resolve({ success: true, data: handler(args || []) });
            } catch (e) {
                resolve({ success: false, error: e.message || 'Request did not go through.', errorCode: e.errorCode || null, details: e.details || null });
            }
        }, NETWORK_DELAY_MS);
    });

export const MOCK_KIOSK_COMMANDS = Object.keys(handlers);
